import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import type { Shift, ShiftInput } from './crew.ts';
import { ApiProblem } from './me.ts';

// CB-REQ CRW-04 and CRW-11 at v1.994 – v1.995, crew part 3d: booking several people at once, and
// offers with places — one offer to several people, the first to accept taking the places. The
// screens call it an offer. Every rule is the database's; these say it in the office's words.

/** An offer as the office sees it: `rate` is there only for someone with prices.view. */
export type Offer = components['schemas']['ShiftOfferCostedView'];
export type OfferRecipient = components['schemas']['OfferRecipientView'];
export type Refused = components['schemas']['SeveralRefusedView'];
/** An offer to me, under the company's pre-confirmation view. Never a rate. */
export type MyOffer = components['schemas']['MyOfferView'];

export type OfferInput = Omit<ShiftInput, 'crewMemberId'> & { places: number };

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

/**
 * One shift each — at a venue or the depot, or with no place as held days — offered now unless told
 * not to. Refusals come back by person.
 */
export async function createSeveral(
  input: Omit<ShiftInput, 'crewMemberId'>,
  crewMemberIds: string[],
  offerNow: boolean,
) {
  const { data, error } = await api.POST('/v1/shifts/several', {
    body: { ...input, crewMemberIds, offerNow },
  });
  if (error) throw problem(error, 'Could not add those shifts.');
  return data as { made: Shift[]; refused: Refused[] };
}

/** At a venue or the depot, booked for a job or none, or with no place as held days. */
export async function createOffer(input: OfferInput, crewMemberIds: string[]) {
  const { data, error } = await api.POST('/v1/shift-offers', {
    body: { ...input, crewMemberIds },
  });
  if (error) throw problem(error, 'Could not make that offer.');
  return data as { offer: Offer; refused: Refused[] };
}

export const jobOffersQuery = (jobId: string) =>
  queryOptions({
    queryKey: ['jobs', jobId, 'offers'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/shift-offers', {
        params: { query: { jobId } },
      });
      if (error) throw problem(error, 'Could not load this job’s offers.');
      return data as Offer[];
    },
  });

/** Offers still taking answers, soonest first: Home and the roster. */
export const openOffersQuery = queryOptions({
  queryKey: ['shift-offers', 'open'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/shift-offers', {
      params: { query: { open: 'true' } },
    });
    if (error) throw problem(error, 'Could not load the open offers.');
    return data as Offer[];
  },
});

export async function updateOffer(id: string, changes: Partial<OfferInput>) {
  const { data, error } = await api.PATCH('/v1/shift-offers/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not save that offer.');
  return data as Offer;
}

export async function closeOffer(id: string) {
  const { data, error } = await api.POST('/v1/shift-offers/{id}/close', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not close that offer.');
  return data as Offer;
}

export async function addToOffer(id: string, crewMemberIds: string[]) {
  const { data, error } = await api.POST('/v1/shift-offers/{id}/recipients', {
    params: { path: { id } },
    body: { crewMemberIds },
  });
  if (error) throw problem(error, 'Could not offer it to them.');
  return data as { offer: Offer; refused: Refused[] };
}

export async function withdrawFromOffer(id: string, crewMemberId: string) {
  const { data, error } = await api.POST(
    '/v1/shift-offers/{id}/recipients/{crewMemberId}/withdraw',
    { params: { path: { id, crewMemberId } } },
  );
  if (error) throw problem(error, 'Could not withdraw it.');
  return data as Offer;
}

export async function reoffer(id: string, crewMemberId: string) {
  const { data, error } = await api.POST(
    '/v1/shift-offers/{id}/recipients/{crewMemberId}/reoffer',
    { params: { path: { id, crewMemberId } } },
  );
  if (error) throw problem(error, 'Could not offer it again.');
  return data as Offer;
}

/** The office recording a person's answer, as they phoned it in (CRW-13). */
export async function answerOffer(
  id: string,
  crewMemberId: string,
  answer: { answer: 'accepted' } | { answer: 'declined'; reason?: string | null },
) {
  const { data, error } = await api.POST('/v1/shift-offers/{id}/recipients/{crewMemberId}/answer', {
    params: { path: { id, crewMemberId } },
    body: answer,
  });
  if (error) throw problem(error, 'Could not record their answer.');
  return data as Offer;
}

// — My offers —

export const myOffersQuery = queryOptions({
  queryKey: ['my', 'offers'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/my/offers');
    if (error) throw problem(error, 'Could not load your offers.');
    return data as MyOffer[];
  },
});

export async function acceptMyOffer(id: string) {
  const { data, error } = await api.POST('/v1/my/offers/{id}/accept', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not accept that.');
  return data as MyOffer;
}

export async function declineMyOffer(id: string, reason: string | null) {
  const { data, error } = await api.POST('/v1/my/offers/{id}/decline', {
    params: { path: { id } },
    body: { reason },
  });
  if (error) throw problem(error, 'Could not decline that.');
  return data as MyOffer;
}

/** "3 places · 1 taken". */
export function placesLabel(o: { places: number; taken: number }): string {
  return `${o.places} ${o.places === 1 ? 'place' : 'places'} · ${o.taken} taken`;
}

/** Who it went to and how each answered, in a word. */
export function answerOf(r: OfferRecipient): 'Taken' | 'Declined' | 'Withdrawn' | 'Waiting' {
  if (r.shift && r.shift.state !== 'cancelled' && r.shift.state !== 'declined') return 'Taken';
  if (r.withdrawnAt) return 'Withdrawn';
  if (r.declinedAt) return 'Declined';
  return 'Waiting';
}
